import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Divider,
  Chip,
  ButtonGroup,
  CircularProgress,
  Skeleton,
  Alert,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { loadPatients } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';

/* ─────────────────────────────────────────────────────────────
   Theme tokens — adapt cleanly to light + black themes.
───────────────────────────────────────────────────────────── */
const usePIxTokens = () => {
  const theme = useTheme();
  return useMemo(() => {
    const isDark = theme.palette.mode === 'dark';
    return {
      isDark,
      pageBg:       isDark ? '#000000' : '#F4F6FA',
      surface:      isDark ? '#0E0E14' : '#FFFFFF',
      surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
      fieldBg:      isDark ? '#14141C' : '#FFFFFF',
      borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
      borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
      text:         isDark ? '#FFFFFF' : '#0F172A',
      textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
      textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
      rowStripe:    isDark ? 'rgba(255,255,255,0.02)' : '#FAFBFC',
      rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
      rowSelected:  isDark ? 'rgba(5,150,105,0.15)' : '#ECFDF5',
      rowSelectedHover: isDark ? 'rgba(5,150,105,0.22)' : '#D1FAE5',
      cardShadow:   isDark
        ? '0 1px 2px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.35)'
        : '0 1px 2px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)',
      accent: {
        emerald: '#059669',  // brand
        blue:    '#3b82f6',
        sky:     '#0ea5e9',
        rose:    '#ec4899',  // for female chip
        indigo:  '#6272ea',
        amber:   '#f59e0b',
        danger:  '#ef4444',
      }
    };
  }, [theme.palette.mode]);
};



const PatientInformation = ({onEditPatient }) => {
  const t = usePIxTokens();

  const [searchBy, setSearchBy] = useState({
    opdNo: true,
    name: false,
    insuranceNo: false,
    phoneNo: false,
    fingerPrint: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [activeFilter, setActiveFilter] = useState('today');
  const [isLoading, setIsLoading] = useState(true);

  const [allPatientData, setallPatientData] = useState([]);
  
  // New state for row selection
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State for Patient Vitals Modal
  const [openPatient, setOpenPatient] = useState(false);
  const [vitalsInfo, setVitalsInfo] = useState(null);

  useEffect(() => {
    AllPatient()
  }, [])

  const AllPatient = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(loadPatients);
      if (response.data.success) {
        setallPatientData(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed Load Data',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleSearchByChange = (field) => {
    setSearchBy(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Enhanced row selection handlers
  const handleRowSelect = (patient) => {
    setSelectedPatient(patient);
    const newSelectedPatients = new Set(selectedPatients);
    
    if (selectedPatients.has(patient.id)) {
      newSelectedPatients.delete(patient.id);
    } else {
      newSelectedPatients.add(patient.id);
    }
    
    setSelectedPatients(newSelectedPatients);
  };

  const handleClearSelection = () => {
    setSelectedPatients(new Set());
    setSelectedPatient(null);
  };

  const handleSelectAll = () => {
    if (selectedPatients.size === searchFilteredData.slice(0, entriesPerPage).length) {
      // If all are selected, clear selection
      handleClearSelection();
    } else {
      // Select all visible patients
      const allVisibleIds = new Set(
        searchFilteredData.slice(0, entriesPerPage).map(patient => patient.id)
      );
      setSelectedPatients(allVisibleIds);
    }
  };

  const handleEditRecord = (patient) => {
    console.log('Edit record for patient:', patient);
    
    // Pass the entire patient object to the parent component
    if (onEditPatient) {
      onEditPatient(patient);
    }
  };



  

  const filterDataByPeriod = (data, period) => {
    const today = new Date();
    
    return data.filter(patient => {
      const visitDate = new Date(patient.reg_date);
      
      switch(period) {
        case 'today':
          // Since reg_date is in YYYY-MM-DD format, get today in the same format
          // Using local date to avoid timezone issues
          const todayStr = today.getFullYear() + '-' + 
                          String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(today.getDate()).padStart(2, '0');
          return patient.reg_date === todayStr;
          
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(today.getDate() - 7);
          return visitDate >= weekAgo;
          
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(today.getMonth() - 1);
          return visitDate >= monthAgo;
          
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(today.getFullYear() - 1);
          return visitDate >= yearAgo;
          
        case 'all':
          return true;
          
        default:
          return true;
      }
    });
  };

  const filteredData = filterDataByPeriod(allPatientData, activeFilter);
  
  const searchFilteredData = filteredData.filter(patient => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const searchFields = [];
    
    // Absolute number search for OPD Number (trans_code)
    if (searchBy.opdNo) {
      if (String(patient.trans_code) === searchTerm) {
        return true;
      }
    }
    
    // Absolute number search for Insurance Number
    if (searchBy.insuranceNo && patient.Insurance) {
      if (String(patient.Insurance) === searchTerm) {
        return true;
      }
    }
    
    // Handle name field (assuming it's always a string)
    if (searchBy.name) {
      searchFields.push(patient.name.toLowerCase());
    }
    
    // Handle phone field (might be string or number)
    if (searchBy.phoneNo) {
      searchFields.push(String(patient.tel).toLowerCase());
    }
    
    return searchFields.some(field => field.includes(searchLower));
  });

  // Loading skeleton component for table rows
  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
          <TableCell><Skeleton variant="text" width={40} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={90} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
 
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, backgroundColor: t.pageBg, minHeight: '100vh' }}>
      {/* Page header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 1.75,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${t.accent.emerald} 0%, ${alpha(t.accent.emerald, 0.82)} 100%)`,
            color: 'white',
            boxShadow: `0 6px 16px ${alpha(t.accent.emerald, 0.35)}`
          }}>
            <PeopleIcon />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.35rem', sm: '1.6rem' }, fontWeight: 700, color: t.text, letterSpacing: '-0.02em', lineHeight: 1.15 }}>
              Patient Information
            </Typography>
            <Typography sx={{ fontSize: '0.88rem', color: t.textMuted, mt: 0.25 }}>
              Welcome to the Patient Information System. Below is the list of your patients.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Selection status banner */}
      {selectedPatients.size > 0 && (
        <Box sx={{
          mb: 3, p: 1.5, borderRadius: 2,
          backgroundColor: alpha(t.accent.emerald, t.isDark ? 0.12 : 0.08),
          border: `1px solid ${alpha(t.accent.emerald, 0.3)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 1,
              backgroundColor: alpha(t.accent.emerald, 0.18),
              color: t.accent.emerald,
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <CheckCircleIcon fontSize="small" />
            </Box>
            <Typography sx={{ color: t.text, fontWeight: 600, fontSize: '0.92rem' }}>
              {selectedPatients.size} patient{selectedPatients.size !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleClearSelection}
            startIcon={<ClearIcon />}
            sx={{
              textTransform: 'none', fontWeight: 600,
              color: t.accent.emerald,
              '&:hover': { backgroundColor: alpha(t.accent.emerald, 0.1) }
            }}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Search Section */}
      <Card sx={{
        mb: 3, borderRadius: 2.5,
        backgroundColor: t.surface, backgroundImage: 'none',
        border: `1px solid ${t.borderSubtle}`,
        boxShadow: t.cardShadow
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: t.accent.emerald, boxShadow: `0 0 10px ${alpha(t.accent.emerald, 0.5)}` }} />
            <Typography sx={{ color: t.text, fontWeight: 700, fontSize: '0.92rem', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Search &amp; Filter Patients
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 2.5 }}>
            {/* Search By Checkboxes */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { key: 'opdNo', label: 'OPD No.' },
                { key: 'name', label: 'Name' },
                { key: 'insuranceNo', label: 'Insurance No.' },
                { key: 'phoneNo', label: 'Phone No.' },
              ].map((item) => (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={searchBy[item.key]}
                      onChange={() => handleSearchByChange(item.key)}
                      disabled={isLoading}
                      sx={{ color: t.accent.emerald, '&.Mui-checked': { color: t.accent.emerald } }}
                    />
                  }
                  label={item.label}
                  sx={{ '& .MuiFormControlLabel-label': { color: t.text, fontWeight: 500, fontSize: '0.88rem' } }}
                />
              ))}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center', borderColor: t.borderSubtle }} />

            {/* Time Filter Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography sx={{ color: t.textMuted, fontWeight: 600, whiteSpace: 'nowrap', fontSize: '0.85rem' }}>
                Filter by Period:
              </Typography>
              <ButtonGroup sx={{ boxShadow: 'none', '& .MuiButton-root': { border: 'none' } }}>
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                  { key: 'year', label: 'This Year' },
                  { key: 'all', label: 'All' }
                ].map((filter) => {
                  const isActive = activeFilter === filter.key;
                  return (
                    <Button
                      key={filter.key}
                      onClick={() => setActiveFilter(filter.key)}
                      disabled={isLoading}
                      sx={{
                        background: isActive
                          ? `linear-gradient(135deg, ${t.accent.emerald} 0%, ${alpha(t.accent.emerald, 0.82)} 100%)`
                          : alpha(t.text, t.isDark ? 0.06 : 0.04),
                        color: isActive ? 'white' : t.text,
                        fontWeight: 600, textTransform: 'none',
                        px: 2, py: 1, fontSize: '0.85rem',
                        boxShadow: isActive ? `0 4px 12px ${alpha(t.accent.emerald, 0.3)}` : 'none',
                        '&:hover': {
                          background: isActive
                            ? `linear-gradient(135deg, ${alpha(t.accent.emerald, 0.92)} 0%, ${alpha(t.accent.emerald, 0.72)} 100%)`
                            : alpha(t.text, t.isDark ? 0.10 : 0.08),
                        },
                        '&.Mui-disabled': {
                          background: alpha(t.text, 0.04),
                          color: t.textDim
                        }
                      }}
                    >
                      {filter.label}
                    </Button>
                  );
                })}
              </ButtonGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              variant="outlined"
              placeholder="Search patients…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
              sx={{
                flexGrow: 1, minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5,
                  backgroundColor: t.fieldBg,
                  '& fieldset': { borderColor: t.borderSubtle },
                  '&:hover fieldset': { borderColor: alpha(t.accent.emerald, 0.5) },
                  '&.Mui-focused fieldset': { borderColor: t.accent.emerald },
                }
              }}
            />
            <IconButton
              disabled={isLoading}
              sx={{
                background: `linear-gradient(135deg, ${t.accent.emerald} 0%, ${alpha(t.accent.emerald, 0.82)} 100%)`,
                color: 'white', borderRadius: 1.5, px: 2,
                boxShadow: `0 4px 12px ${alpha(t.accent.emerald, 0.3)}`,
                '&:hover': { boxShadow: `0 6px 16px ${alpha(t.accent.emerald, 0.4)}` },
                '&.Mui-disabled': { background: alpha(t.text, 0.08), color: t.textDim, boxShadow: 'none' }
              }}
            >
              <SearchIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: t.textMuted, fontWeight: 500, fontSize: '0.88rem' }}>Show</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(e.target.value)}
                  disabled={isLoading}
                  sx={{
                    borderRadius: 1.5,
                    backgroundColor: t.fieldBg,
                    '& fieldset': { borderColor: t.borderSubtle }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                  <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ color: t.textMuted, fontWeight: 500, fontSize: '0.88rem' }}>entries</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Patient Table */}
      <Card sx={{
        borderRadius: 2.5,
        backgroundColor: t.surface, backgroundImage: 'none',
        border: `1px solid ${t.borderSubtle}`,
        boxShadow: t.cardShadow,
        overflow: 'hidden'
      }}>
        <TableContainer component={Paper} sx={{ borderRadius: 0, backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: t.surfaceAlt }}>
                <TableCell padding="checkbox" sx={{ borderColor: t.borderSubtle }}>
                  <Checkbox
                    indeterminate={
                      selectedPatients.size > 0 &&
                      selectedPatients.size < searchFilteredData.slice(0, entriesPerPage).length
                    }
                    checked={
                      searchFilteredData.slice(0, entriesPerPage).length > 0 &&
                      selectedPatients.size === searchFilteredData.slice(0, entriesPerPage).length
                    }
                    onChange={handleSelectAll}
                    disabled={isLoading || searchFilteredData.length === 0}
                    sx={{ color: t.accent.emerald, '&.Mui-checked': { color: t.accent.emerald }, '&.MuiCheckbox-indeterminate': { color: t.accent.emerald } }}
                  />
                </TableCell>
                {['Patient ID', 'Name', 'Gender', 'Age', 'Insurance #', 'Address', 'Telephone', 'Occupation', 'Visit Date', 'Actions'].map((label, i) => (
                  <TableCell key={label} sx={{
                    fontWeight: 700, color: t.text,
                    fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: 0.5,
                    borderColor: t.borderSubtle,
                    ...(label === 'Actions' && { textAlign: 'center' })
                  }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <LoadingSkeleton />
              ) : searchFilteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ textAlign: 'center', py: 6, borderColor: t.borderSubtle }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Box sx={{
                        width: 48, height: 48, borderRadius: 2,
                        backgroundColor: alpha(t.accent.emerald, 0.12),
                        color: t.accent.emerald,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                      }}>
                        <PeopleIcon />
                      </Box>
                      <Typography sx={{ color: t.textMuted, fontSize: '0.95rem' }}>
                        {searchTerm ? 'No patients found matching your search criteria' : 'No patient records found for this period'}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                searchFilteredData.slice(0, entriesPerPage).map((patient) => {
                  const isMale = patient.gender === 'Male' || patient.gender === 'MALE';
                  const genderColor = isMale ? t.accent.blue : t.accent.rose;
                  return (
                    <TableRow
                      key={patient.id}
                      selected={selectedPatients.has(patient.id)}
                      sx={{
                        '& .MuiTableCell-root': { color: t.text, borderColor: t.borderSubtle },
                        '&:hover': { backgroundColor: t.rowHover },
                        '&:nth-of-type(even)': { backgroundColor: t.rowStripe },
                        '&.Mui-selected': {
                          backgroundColor: `${t.rowSelected} !important`,
                          '&:hover': { backgroundColor: `${t.rowSelectedHover} !important` }
                        }
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedPatients.has(patient.id)}
                          onChange={() => handleRowSelect(patient)}
                          sx={{ color: t.accent.emerald, '&.Mui-checked': { color: t.accent.emerald } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: `${t.accent.blue} !important` }}>{patient.id}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{patient.fullName}</TableCell>
                      <TableCell>
                        <Chip
                          label={patient.gender}
                          size="small"
                          sx={{
                            backgroundColor: alpha(genderColor, t.isDark ? 0.18 : 0.14),
                            color: genderColor,
                            border: `1px solid ${alpha(genderColor, 0.3)}`,
                            fontWeight: 700,
                            fontSize: '0.72rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>{patient.age}</TableCell>
                      <TableCell>{patient.Insurance}</TableCell>
                      <TableCell>{patient.res}</TableCell>
                      <TableCell>{patient.tel}</TableCell>
                      <TableCell>{patient.occ}</TableCell>
                      <TableCell sx={{ color: `${t.accent.emerald} !important`, fontWeight: 600 }}>{patient.reg_date}</TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton
                            onClick={() => handleEditRecord(patient)}
                            sx={{
                              backgroundColor: alpha(t.accent.sky, t.isDark ? 0.18 : 0.12),
                              color: t.accent.sky,
                              borderRadius: 1.5, p: 0.75,
                              '&:hover': {
                                backgroundColor: alpha(t.accent.sky, t.isDark ? 0.28 : 0.20),
                              }
                            }}
                            title={`Edit ${patient.fullName}'s record`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{
          p: 2.5, backgroundColor: t.surfaceAlt,
          borderTop: `1px solid ${t.borderSubtle}`
        }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={16} sx={{ color: t.accent.emerald }} />
              <Typography variant="body2" sx={{ color: t.textMuted, fontWeight: 500 }}>
                Loading patient records…
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" sx={{ color: t.textMuted, fontWeight: 500 }}>
                Showing {Math.min(searchFilteredData.length, entriesPerPage)} of {searchFilteredData.length} entries
              </Typography>
              {selectedPatients.size > 0 && (
                <Chip
                  label={`${selectedPatients.size} selected`}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: alpha(t.accent.emerald, t.isDark ? 0.18 : 0.14),
                    color: t.accent.emerald,
                    border: `1px solid ${alpha(t.accent.emerald, 0.3)}`,
                    fontWeight: 700, fontSize: '0.72rem'
                  }}
                />
              )}
              {activeFilter !== 'today' && (
                <Chip
                  label={`Filtered by: ${activeFilter}`}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: alpha(t.accent.blue, t.isDark ? 0.18 : 0.14),
                    color: t.accent.blue,
                    border: `1px solid ${alpha(t.accent.blue, 0.3)}`,
                    fontWeight: 700, fontSize: '0.72rem'
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default PatientInformation;